import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CpuChipIcon,
  BeakerIcon,
  ClipboardDocumentListIcon,
  CalendarDaysIcon,
} from '@heroicons/react/24/outline';
import { getAssets } from '../../../api/assets';
import { listTasks } from '../../../api/workTask';
import { formatTaskStatus } from '../../../utils/workTaskStatusLabels';
import { isLoggedIn } from '../../../utils/token.js';
import { resolveTenantId } from '../../../utils/tenantParams.js';
import { shortLived } from '../../../utils/shortLivedCache';
import { computeDeviceStatusStats } from '../../../api/welcomeShellData';
import {
  isSoilSensorDevice,
  parseSoilLatestStatus,
} from '../../../components/farm-smart-device/mordeniotSoilDevice';
import { buildBusinessContextPath, EMPTY_COPY } from '../../../utils/businessContextNav';

/* ==================== 设备状态卡片（设备由 WelcomePage 单次加载） ==================== */
export const DeviceStatusCard = ({ devices = [], iotDevices = [], loading = false, farmId = '', landId = '', deviceId = '' }) => {
  const navigate = useNavigate();
  const go = (path, extra = {}) =>
    navigate(buildBusinessContextPath(path, { farmId, landId, deviceId, ...extra }));
  const stats = useMemo(
    () => computeDeviceStatusStats(devices, iotDevices),
    [devices, iotDevices]
  );

  const { total, online, offline, alert } = stats;
  const onlinePercent = total > 0 ? Math.round((online / total) * 100) : 0;
  const offlinePercent = total > 0 ? Math.round((offline / total) * 100) : 0;
  const alertPercent = total > 0 ? Math.round((alert / total) * 100) : 0;

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-col">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-semibold text-gray-800 flex items-center gap-1.5">
          <CpuChipIcon className="h-4 w-4 text-green-600" aria-hidden />设备状态
        </h4>
        <button type="button" onClick={() => go('/production/land/smart-device')} className="text-sm text-gray-600 hover:text-green-700">
          更多
        </button>
      </div>

      {loading ? (
        <div className="flex-1 flex items-center justify-center py-6">
          <div className="animate-spin h-6 w-6 border-2 border-green-200 border-t-green-600 rounded-full" />
        </div>
      ) : (
        <div className="flex items-center gap-3 flex-1">
          <div className="relative w-16 h-16 shrink-0">
            <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90">
              <path
                d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831a 15.9155 15.9155 0 0 1 0 -31.831"
                fill="none"
                stroke="#e5e7eb"
                strokeWidth="3"
              />
              <path
                d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831a 15.9155 15.9155 0 0 1 0 -31.831"
                fill="none"
                stroke="#22c55e"
                strokeWidth="3"
                strokeDasharray={`${onlinePercent}, 100`}
                strokeLinecap="round"
              />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center">
                <div className="text-xs font-medium text-gray-600">总设备</div>
                <div className="text-xl font-bold text-green-600">{total}<span className="text-xs text-gray-600 font-normal">台</span></div>
              </div>
            </div>
          </div>

          <div className="flex-1 space-y-1.5">
            <div className="flex items-center justify-between text-xs">
              <span className="flex items-center gap-1"><span className="w-2 h-2 bg-green-500 rounded-full" />在线</span>
              <span className="font-medium text-gray-700">{online}台 ({onlinePercent}%)</span>
            </div>
            <div className="flex items-center justify-between text-xs">
              <span className="flex items-center gap-1"><span className="w-2 h-2 bg-gray-400 rounded-full" />离线</span>
              <span className="font-medium text-gray-700">{offline}台 ({offlinePercent}%)</span>
            </div>
            <div className="flex items-center justify-between text-xs">
              <span className="flex items-center gap-1"><span className="w-2 h-2 bg-red-500 rounded-full" />故障</span>
              <span className="font-medium text-gray-700">{alert}台 ({alertPercent}%)</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ==================== 土壤墒情卡片（按地块 · 设备近时） ==================== */
const MOISTURE_DRY_PCT = 40;
const MOISTURE_WET_PCT = 80;
const SOIL_BIND_PATH = '/production/land/smart-device';
const SOIL_MORE_PATH = '/production/land/env-monitoring';
const UNBOUND_LAND_KEY = '__unbound__';

function moistureLevelOf(pct) {
  if (pct == null || Number.isNaN(Number(pct))) {
    return { label: '—', color: 'text-gray-400' };
  }
  const v = Number(pct);
  if (v >= MOISTURE_WET_PCT) return { label: '过湿', color: 'text-blue-600' };
  if (v >= MOISTURE_DRY_PCT) return { label: '适宜', color: 'text-green-600' };
  return { label: '干旱', color: 'text-red-600' };
}

function parseLandAssetList(res) {
  const body = res?.data;
  if (body?.success && Array.isArray(body.data)) return body.data;
  return [];
}

function formatMoisturePct(pct) {
  if (pct == null || Number.isNaN(Number(pct))) return null;
  const v = Number(pct);
  return Number.isInteger(v) ? String(v) : v.toFixed(1);
}

/** 从智能设备台账筛出墒情设备（SOIL_SENSOR / SOIL_STATION / ModernIoT） */
function collectSoilDevices(devices) {
  const map = new Map();
  (Array.isArray(devices) ? devices : []).forEach((d) => {
    if (!d || !isSoilSensorDevice(d)) return;
    const id = d.id != null ? String(d.id) : '';
    if (!id || map.has(id)) return;
    map.set(id, d);
  });
  return [...map.values()];
}

function buildLandNameMap(lands) {
  const map = new Map();
  (lands || []).forEach((l) => {
    if (!l?.id) return;
    const name = l.parcelName || l.assetName || l.landName || l.name || String(l.id);
    map.set(String(l.id), name);
  });
  return map;
}

/**
 * 按地块分组：有近时墒情的设备展示数值与适宜/干旱/过湿；
 * 离线且无近时读数的设备标离线；未绑地块单独分组。
 */
function groupSoilByLand(devices, landNameById) {
  const groups = new Map();

  devices.forEach((d) => {
    const status = parseSoilLatestStatus(d);
    const moisture = status.soilMoisture;
    const online = d.onlineStatus === true || status.online === true
      || String(status.status || '').toUpperCase() === 'ONLINE';
    const landId = d.landId != null && String(d.landId).trim() !== ''
      ? String(d.landId).trim()
      : UNBOUND_LAND_KEY;
    const landName = landId === UNBOUND_LAND_KEY
      ? '未绑定地块'
      : (landNameById.get(landId) || d.landName || d.assetName || landId);
    const deviceName = d.deviceName || d.name || status.name || d.deviceCode || d.serialNumber || '墒情设备';

    if (!groups.has(landId)) {
      groups.set(landId, { landId, landName, devices: [] });
    }
    groups.get(landId).devices.push({
      id: String(d.id),
      deviceName,
      moisture,
      online,
      level: moistureLevelOf(moisture),
    });
  });

  return [...groups.values()].sort((a, b) => {
    if (a.landId === UNBOUND_LAND_KEY) return 1;
    if (b.landId === UNBOUND_LAND_KEY) return -1;
    return String(a.landName).localeCompare(String(b.landName), 'zh');
  });
}

export const SoilMoistureCard = ({ devices = [], loading: devicesLoading = false, farmId = '', landId = '', deviceId = '' }) => {
  const navigate = useNavigate();
  const go = (path, extra = {}) =>
    navigate(buildBusinessContextPath(path, { farmId, landId, deviceId, ...extra }));
  const [landLoading, setLandLoading] = useState(false);
  const [landNameById, setLandNameById] = useState(() => new Map());

  const soilDevices = useMemo(() => collectSoilDevices(devices), [devices]);

  // 仅当地块名缺失时再拉一次地块资产，不再重复请求设备列表
  useEffect(() => {
    let cancelled = false;
    if (!isLoggedIn() || !soilDevices.length) {
      setLandNameById(new Map());
      setLandLoading(false);
      return undefined;
    }
    const needLandNames = soilDevices.some((d) => {
      const landId = d?.landId != null ? String(d.landId).trim() : '';
      if (!landId) return false;
      return !(d.landName || d.assetName);
    });
    if (!needLandNames) {
      setLandNameById(new Map());
      setLandLoading(false);
      return undefined;
    }

    setLandLoading(true);
    const tenantId = resolveTenantId() || 'default';
    shortLived(
      `home:land-assets:${tenantId}`,
      () => getAssets({ tenantId, page: 0, size: 200, assetType: 'LAND' }).catch(() => null),
      20000
    )
      .then((landRes) => {
        if (cancelled) return;
        setLandNameById(buildLandNameMap(parseLandAssetList(landRes)));
      })
      .catch(() => {
        if (!cancelled) setLandNameById(new Map());
      })
      .finally(() => {
        if (!cancelled) setLandLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [soilDevices]);

  const groups = useMemo(
    () => groupSoilByLand(soilDevices, landNameById),
    [soilDevices, landNameById]
  );
  const deviceCount = soilDevices.length;
  const loading = devicesLoading || landLoading;

  const hasLiveReading = groups.some((g) =>
    g.devices.some((d) => d.moisture != null || d.online)
  );
  const allOfflineNoData = deviceCount > 0 && !hasLiveReading;

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-col min-h-[140px]">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-semibold text-gray-800 flex items-center gap-1.5">
          <BeakerIcon className="h-4 w-4 text-sky-600" aria-hidden />土壤墒情
        </h4>
        <button
          type="button"
          onClick={() => go(SOIL_MORE_PATH)}
          className="text-sm text-gray-600 hover:text-green-700"
        >
          更多
        </button>
      </div>

      {loading ? (
        <div className="flex-1 flex items-center justify-center py-6">
          <div className="animate-spin h-6 w-6 border-2 border-sky-200 border-t-sky-600 rounded-full" />
        </div>
      ) : deviceCount === 0 || allOfflineNoData ? (
        <div className="flex-1 flex flex-col items-center justify-center text-center gap-2 py-2">
          <p className="text-sm text-gray-600">
            {deviceCount === 0 ? EMPTY_COPY.NOT_CONNECTED : EMPTY_COPY.NO_RECORD}
          </p>
          <button
            type="button"
            onClick={() => go(SOIL_BIND_PATH)}
            className="text-sm font-semibold text-sky-700 hover:text-sky-900"
          >
            去补绑设备
          </button>
        </div>
      ) : (
        <div className="flex-1 space-y-2 overflow-y-auto max-h-[160px]">
          {groups.map((g) => (
            <div key={g.landId} className="space-y-1">
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-semibold text-gray-700 truncate">{g.landName}</span>
                {g.landId === UNBOUND_LAND_KEY ? (
                  <button
                    type="button"
                    onClick={() => go(SOIL_BIND_PATH, g.landId !== UNBOUND_LAND_KEY ? { landId: g.landId } : {})}
                    className="text-xs text-sky-700 hover:text-sky-900 shrink-0"
                  >
                    补绑地块
                  </button>
                ) : null}
              </div>
              {g.devices.map((d) => {
                const pctText = formatMoisturePct(d.moisture);
                const showOffline = !d.online && pctText == null;
                return (
                  <div
                    key={d.id}
                    className="flex items-center justify-between gap-2 py-0.5 px-1 rounded hover:bg-gray-50"
                  >
                    <span className="text-xs text-gray-600 truncate flex-1 min-w-0">{d.deviceName}</span>
                    {showOffline ? (
                      <span className="text-xs text-gray-600 shrink-0">离线</span>
                    ) : (
                      <span className="flex items-center gap-1.5 shrink-0">
                        <span className="text-sm font-bold text-gray-800 tabular-nums">
                          {pctText != null ? `${pctText}%` : '—'}
                        </span>
                        <span className={`text-xs font-medium ${d.level.color}`}>{d.level.label}</span>
                        {!d.online && pctText != null ? (
                          <span className="text-xs text-gray-600">近时</span>
                        ) : null}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
          <div className="flex items-center gap-2 pt-2 text-xs text-gray-600 border-t border-gray-100">
            <span>干旱 &lt;{MOISTURE_DRY_PCT}%</span>
            <span>适宜 {MOISTURE_DRY_PCT}%~{MOISTURE_WET_PCT}%</span>
            <span>过湿 &gt;{MOISTURE_WET_PCT}%</span>
          </div>
        </div>
      )}
    </div>
  );
};

/* ==================== 我的工作任务卡片（work-task 模块） ==================== */
function taskStatusColor(status) {
  const s = String(status || '').toUpperCase();
  if (s === 'IN_PROGRESS') return 'bg-blue-100 text-blue-700';
  if (s === 'DONE') return 'bg-gray-100 text-gray-600';
  return 'bg-green-100 text-green-700';
}

export const TodayTasksCard = ({ deferMs = 320 } = {}) => {
  const navigate = useNavigate();
  // 静默：默认不转圈，空态先展示，数据就绪后填入
  const [loading, setLoading] = useState(false);
  const [tasks, setTasks] = useState([]);

  useEffect(() => {
    let cancelled = false;
    let timer = 0;
    let idleId = 0;
    const today = new Date().toISOString().slice(0, 10);

    const load = async () => {
      if (!isLoggedIn()) {
        if (!cancelled) setTasks([]);
        return;
      }
      try {
        const res = await listTasks({ startDate: today, endDate: today, page: 0, size: 5 });
        const content = res?.data?.content ?? [];
        if (!cancelled) {
          setTasks(Array.isArray(content) ? content : []);
        }
      } catch {
        if (!cancelled) setTasks([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    const start = () => {
      if (!cancelled) load();
    };
    timer = window.setTimeout(() => {
      if (typeof window.requestIdleCallback === 'function') {
        idleId = window.requestIdleCallback(start, { timeout: 2000 });
      } else {
        start();
      }
    }, deferMs);

    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
      if (idleId && typeof window.cancelIdleCallback === 'function') {
        window.cancelIdleCallback(idleId);
      }
    };
  }, [deferMs]);

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-col min-h-[140px]">
      <div className="flex items-center justify-between gap-3 mb-3">
        <h4 className="text-sm font-semibold text-gray-800 flex items-center gap-1.5">
          <ClipboardDocumentListIcon className="h-4 w-4 text-green-600" aria-hidden />我的工作任务
        </h4>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate('/my-daily-reports')}
            className="text-sm text-gray-600 hover:text-green-700"
          >
            我的日报
          </button>
          <button
            type="button"
            onClick={() => navigate('/my-work-tasks')}
            className="text-sm text-gray-600 hover:text-green-700"
          >
            更多
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex-1 flex items-center justify-center text-sm text-gray-600">加载中...</div>
      ) : tasks.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center text-center gap-2">
          <p className="text-sm text-gray-600">{EMPTY_COPY.NO_RECORD}</p>
          <button
            type="button"
            onClick={() => navigate('/my-work-tasks')}
            className="text-sm font-semibold text-green-700 hover:text-green-800"
          >
            查看我的工作任务
          </button>
        </div>
      ) : (
        <div className="flex-1 space-y-2">
          {tasks.map((task) => (
            <button
              key={task.id}
              type="button"
              onClick={() => navigate('/my-work-tasks')}
              className="w-full flex items-center justify-between py-1.5 text-left hover:bg-gray-50 rounded px-1 -mx-1"
            >
              <span className="text-sm text-gray-700 truncate flex-1">{task.title}</span>
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium shrink-0 ml-2 ${taskStatusColor(task.status)}`}>
                {formatTaskStatus(task.status)}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/* ==================== 农事日历卡片 ==================== */
export const FarmCalendarCard = ({ farmId = '', landId = '', deviceId = '' }) => {
  const navigate = useNavigate();
  const today = new Date();
  const year = today.getFullYear();
  const month = today.getMonth();
  const todayDate = today.getDate();

  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const calendarDays = [];
  for (let i = 0; i < firstDay; i++) calendarDays.push(null);
  for (let i = 1; i <= daysInMonth; i++) calendarDays.push(i);

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-col">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-semibold text-gray-800 flex items-center gap-1.5">
          <CalendarDaysIcon className="h-4 w-4 text-green-600" aria-hidden />农事日历
        </h4>
        <button
          type="button"
          onClick={() =>
            navigate(
              buildBusinessContextPath('/production/field-work/calendar', { farmId, landId, deviceId })
            )
          }
          className="text-sm text-gray-600 hover:text-green-700"
        >
          更多
        </button>
      </div>

      <div className="text-sm text-gray-700 font-medium text-center mb-2">
        {year}年{month + 1}月
      </div>

      <div className="grid grid-cols-7 gap-0.5 text-center">
        {['日', '一', '二', '三', '四', '五', '六'].map((d) => (
          <div key={d} className="text-xs text-gray-600 py-0.5">{d}</div>
        ))}
        {calendarDays.map((day, i) => (
          <div
            key={i}
            className={`text-xs py-1 rounded ${
              day === todayDate
                ? 'bg-green-600 text-white font-semibold'
                : day
                  ? 'text-gray-700 hover:bg-gray-50'
                  : ''
            }`}
          >
            {day || ''}
          </div>
        ))}
      </div>
    </div>
  );
};
